/* eslint-disable @typescript-eslint/ban-ts-comment */
import { AxiosError } from 'axios';
import { appInsightsClient } from '../services/appInsightFactory/appInsightFactory';
import { SeverityLevel } from '../enums/enum';

// Exporting just for testing purpose
export const getStructuredLogMessage = (
  message: string | undefined,
  moduleName: string,
  functionName: string,
) => {
  // Done used in logError and logInformation functions in this file only
  message = `Message=${message}`;
  moduleName = `ModuleName=${moduleName}`;
  functionName = `FunctionName=${functionName}`;

  return message + '\n' + moduleName + '\n' + functionName;
};

// Exporting just for testing purpose
export const getDefaultCustomDimensions = () => {
  // Done used in logError and logInformation functions in this file only
  return { Application: 'FO\\\\AirlineOps UI' };
};

export const getErrorMessage = (error: Error | AxiosError) => {
  // status 0 can be due to 500 Internal Server Error or CORS Policy Error
  if (error) {
    if ('code' in error) {
      return (
        error?.code ??
        error?.message ??
        'No error message or code can be retrieved'
      );
    } else {
      return error?.message || 'No error message or code can be retrieved';
    }
  }
  return 'No error message or code can be retrieved';
};

export const createErrorTelemetryProperties = (
  error: Error | AxiosError | undefined,
) => {
  // returns null if not an object.
  return error || null;
};

export const trackPageView = (pageName: string, email: string | undefined) => {
  const pageView = {
    name: pageName,
  };

  //@ts-ignore
  pageView['properties'] = {
    email: email ?? 'No email can be retrieved',
  };

  appInsightsClient.trackPageView(pageView);
};

/**
 * @description Writes to the App Insights customEvents table
 * Used to track any custom event, such as a button click
 * @param {string} eventName - name of event
 */
export const trackEvent = (eventName: string, email: string | undefined) => {
  const event = {
    name: eventName,
  };

  //@ts-ignore
  event['properties'] = {
    email: email ?? 'No email can be retrieved',
  };

  appInsightsClient.trackEvent(event);
};

export const trackError = (error: Error | AxiosError | undefined) => {
  const errorData = {
    error,
  };

  // status 0 can be due to 500 Internal Server Error or CORS Policy Error
  if (error) {
    let status = null;

    // Check for the response in the error object
    // @ts-ignore
    if (error?.response?.status) {
      // @ts-ignore
      status = error?.response?.status;
    }

    // If response not found, check for the request in the error object
    // @ts-ignore
    else if (error?.request?.status >= 0) {
      // @ts-ignore
      status = error?.request?.status;
    }
    // If both status and request not found, give a custome status
    else {
      status = 'No status can be retrieved';
    }

    //@ts-ignore
    errorData['properties'] = {
      // @ts-ignore
      code: error?.code || 'No code can be retrieved',
      message: error?.message || 'No message can be retrieved',
      // @ts-ignore
      url: error?.config?.url || 'No url can be retrieved',
      // @ts-ignore
      status,
      stackTrace: error?.stack ?? 'No stackTrace can be retrieved',
      completeError: error,
    };
  }

  appInsightsClient.trackException(errorData);
};

export const logInformation = (
  message: string,
  moduleName: string,
  functionName: string,
) => {
  const customProperties = getDefaultCustomDimensions();
  appInsightsClient.trackTrace(
    {
      message: getStructuredLogMessage(message, moduleName, functionName),
      severityLevel: SeverityLevel.INFORMATION,
    },
    customProperties,
  );
};

export const logError = (
  message: string | undefined,
  moduleName: string,
  functionName: string,
  customProperties: object | null = null,
) => {
  customProperties = { ...customProperties, ...getDefaultCustomDimensions() };

  appInsightsClient.trackTrace(
    {
      message: getStructuredLogMessage(message, moduleName, functionName),
      severityLevel: SeverityLevel.ERROR,
    },
    customProperties,
  );
};

export const setAuthenticatedUser = (userEmail: string) => {
  if (userEmail) {
    // @ts-ignore
    appInsightsClient.setAuthenticatedUserContext(userEmail, null, true);
  }
};